import { defineStore } from 'pinia'
import { toValue } from 'vue'
import { useStore } from '.'
import { orders, overview, wallet } from '~/apis/spotSocket'
import { getCurrencyIntro, getFinanceTransferTypeList, getMarketTrades,getSpotCurrency } from '~/apis/spot'

import { time2Date } from '~/utils/tools'
import { getHeaderLang } from '~/utils/locale'

export const useSpotStore = defineStore('spot', {
  state: () => ({
    defaultCode: 'BTCUSDT',
    code: '',
    marketTrades: [],
    favs: new Set(), // 自选
    bbo: {},
    transferTypeRaw: {},
    transferTypeList: [],
    step: null, // orderBook 深度
    currencyIntro: {}, // 币种简介
    modal: {
      cancelAll: false,
    },
    spotCurrencyOptions:[], // 计价币种列表
  }),
  getters: {
    curInfo(s) {
      return this.getInfo(s.code)
    },
    getInfo() {
      const { symbolsRaw, spotCurrenciesRaw } = useStore()
      return code => symbolsRaw[code] ?? spotCurrenciesRaw[code] ?? {}
    },
    base() {
      return this.curInfo.base ?? ''
    },
    quote() {
      return this.curInfo.quote ?? 'USDT'
    },
    as() {
      return this.curInfo.as
    },
    ps() {
      return this.curInfo.ps
    },
    id() {
      return this.curInfo.id
    },
    overview() {
      return toValue(overview)[this.id] ?? {}
    },
    getOverviewByCode() {
      return (code) => {
        const { id } = this.getInfo(code)
        return toValue(overview)[id] ?? {}
      }
    },
    steps() {
      return this.curInfo?.steps?.replace(/\s/g, '').split(',')
    },
    getBaseIconByCode() {
      const { spotCurrenciesRaw } = useStore()
      return code => spotCurrenciesRaw[code]?.iconAddress
    },
    symbolRegions() {
      const { publicData, visibleSymbols } = useStore()
      const o = visibleSymbols?.reduce((sofar, next) => {
        next.regions?.forEach((i) => {
          sofar[i] ??= []
          sofar[i].push(next)
        })

        return sofar
      }, {}) ?? {}
      const restLocale = getRestLocale(getHeaderLang())
      const list = publicData.spotSymbols?.regionsListNew?.map(i => ({ ...i, list: o[i.key] ?? [], name: i[restLocale] ?? i.en_US })) ?? []
      return list
    },
    symbolRegionsRaw() {
      return this.symbolRegions.reduce((sofar, next) => {
        sofar[next.key] = next
        return sofar
      }, {})
    },
    favIds() {
      return useStore().favRaw.symbolIdList ?? []
    },
    orderByCode() {
      return orders.value.os.reduce((sofar, next) => {
        const o = {
          ...next,
          code: next.symbol,
          side: next.order.side,
        }

        sofar[next.symbol] ??= []
        sofar[next.symbol].push(o)

        sofar.ALL.push(o)
        return sofar
      }, { ALL: [] })
    },
    orders() {
      return this.orderByCode.ALL
    },
    ordersN() {
      return this.orders.length
    },
    getBalanceByCode() {
      return (code = this.quote) => {
        if (code === '_ALL') {
          return wallet.value._ALL
        }
        const { id } = this.getInfo(code)
        return wallet.value[id] ?? {}
      }
    },
  },
  actions: {
    async getOverview() {
      const { data } = await useFetch('/api/getSpotOverview')
      overview.value = data.value
    },
    async getFinanceTransferTypeList(type = 2) {
      const { success, data } = await getFinanceTransferTypeList({ type })
      if (success) {
        this.transferTypeList = data.map(i => ({
          ...i,
          label: i.desc,
          key: i.code,
        }))
        this.transferTypeRaw = data.reduce((acc, next) => {
          acc[next.code] = next.desc
          return acc
        })
      }
    },
    async getMarketTrades({ code, ...rest } = { code: this.code ?? '' }) {
      const { success, data } = await getMarketTrades(code.toLocaleLowerCase(), rest ?? {})
      if (success) {
        this.marketTrades = data
          .map((t) => {
            const { time, ts, takerSide, symbol } = t
            return {
              id: ts,
              code: symbol,
              tm: time2Date(time, 'h:i:s'),
              side: takerSide === 'SELL' ? -1 : 1,
              ...t,
            }
          })
          .reverse()
      }
    },
    async getSpotCurrency(){
      const { success, data } = await getSpotCurrency()
      if (success) {
        this.spotCurrencyOptions = [
            ...data.map((d) => ({
              label: d.name.toUpperCase(),
              value: d.id,
            }))
          ];
      }
    },
    loadFavSymbols() {
      let arr = []
      try {
        arr = JSON.parse(localStorage.getItem('favSpotIds')).split(',')
      }
      catch (e) {}
      arr.forEach(i => this.favs.add(Number(i)))
    },
    toggleFavSymbols(id) {
      if (this.favs.has(id)) {
        this.favs.delete(id)
      }
      else {
        this.favs.add(id)
      }
      localStorage.setItem('favSpotIds', JSON.stringify([...this.favs].join(',')))
    },
    async getCurrencyIntro(currencyId) {
      const { success, data } = await getCurrencyIntro({ currencyId })
      if (success) {
        this.currencyIntro = data ?? {}
      }
    },
  },
})
